"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Book, Droplets } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

const cupsCards = [
  { name: "Ace of Cups", slug: "ace-of-cups", turkish: "Kupalar Ası", keyword: "Yeni duygusal başlangıçlar", image: "ace-of-cups.svg" },
  { name: "Two of Cups", slug: "two-of-cups", turkish: "İki Kupa", keyword: "Birlik ve uyum", image: "two-of-cups.svg" },
  { name: "Three of Cups", slug: "three-of-cups", turkish: "Üç Kupa", keyword: "Kutlama ve dostluk", image: "three-of-cups.svg" },
  { name: "Four of Cups", slug: "four-of-cups", turkish: "Dört Kupa", keyword: "Meditasyon ve içe dönüş", image: "four-of-cups.svg" },
  { name: "Five of Cups", slug: "five-of-cups", turkish: "Beş Kupa", keyword: "Kayıp ve üzüntü", image: "five-of-cups.svg" },
  { name: "Six of Cups", slug: "six-of-cups", turkish: "Altı Kupa", keyword: "Nostalji ve geçmiş", image: "six-of-cups.svg" },
  { name: "Seven of Cups", slug: "seven-of-cups", turkish: "Yedi Kupa", keyword: "İllüzyon ve seçenekler", image: "seven-of-cups.svg" },
  { name: "Eight of Cups", slug: "eight-of-cups", turkish: "Sekiz Kupa", keyword: "Arayış ve terk etme", image: "eight-of-cups.svg" },
  { name: "Nine of Cups", slug: "nine-of-cups", turkish: "Dokuz Kupa", keyword: "Tatmin ve memnuniyet", image: "nine-of-cups.svg" },
  { name: "Ten of Cups", slug: "ten-of-cups", turkish: "On Kupa", keyword: "Duygusal tamamlanma", image: "ten-of-cups.svg" },
  { name: "Page of Cups", slug: "page-of-cups", turkish: "Kupalar Prensi", keyword: "Duygusal mesajlar", image: "page-of-cups.svg" },
  { name: "Knight of Cups", slug: "knight-of-cups", turkish: "Kupalar Şövalyesi", keyword: "Romantik idealizm", image: "knight-of-cups.svg" },
  { name: "Queen of Cups", slug: "queen-of-cups", turkish: "Kupalar Kraliçesi", keyword: "Sezgisel bilgelik", image: "queen-of-cups.svg" },
  { name: "King of Cups", slug: "king-of-cups", turkish: "Kupalar Kralı", keyword: "Duygusal olgunluk", image: "king-of-cups.svg" }
];

export default function CupsSuitPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-blue-300">
            <Book className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Kupalar</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Kupalar</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Droplets className="h-10 w-10 text-blue-400" />
            Kupalar (Cups) Suit'i
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Su elementi ile ilişkilendirilen Kupalar suit'i, duygular, ilişkiler, sezgi ve
                ruhsal derinlik alanlarını temsil eder. Aşk, empati ve duygusal deneyimler konularında rehberlik sunar.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Su Elementi</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Duygular</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">İlişkiler</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Sezgi</span>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-blue-500/30">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Temel Özellikler</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Element:</strong> Su</li>
                <li>• <strong>Kişilik:</strong> Phlegmatic (Soğuk ve Nemli)</li>
                <li>• <strong>Astrolojik İlişki:</strong> Yengeç, Akrep, Balık</li>
                <li>• <strong>Yaşam Alanı:</strong> Aşk, aile, duygusal iyileşme</li>
                <li>• <strong>Anahtar Kelimeler:</strong> Empati, sezgi, duygu</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {cupsCards.map((card, index) => (
            <Link
              key={card.slug}
              href={`/about/tarot/systems/rider-waite/minor-arcana/cups/${card.slug}`}
              className="block"
            >
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-blue-500/50 transition-all duration-300 cursor-pointer group shadow-lg hover:shadow-xl hover:scale-[1.02] h-full">
                <div className="text-center">
                  <div className="w-24 h-36 mx-auto mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300 shadow-lg">
                    <img
                      src={`/images/tarot/cards/${card.image}`}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to numbered circle if image fails to load
                        e.currentTarget.style.display = 'none';
                        const sibling = e.currentTarget.nextElementSibling as HTMLElement;
        if (sibling) {
          sibling.style.display = 'flex';
        }
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-cyan-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                      <span className="text-white font-bold text-lg">{index + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-100 mb-2">{card.name}</h3>
                  <p className="text-slate-400 text-sm mb-3">{card.turkish}</p>
                  <p className="text-blue-400 text-sm font-medium">{card.keyword}</p>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <span className="text-blue-400 text-sm group-hover:translate-x-1 transition-transform duration-300 inline-block">
                    Kartı İncele →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Suit Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4">Kupalar Suit'inin Anlamı</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">Pozitif Yönler</h3>
                <ul className="space-y-1 text-slate-300 text-sm">
                  <li>• Derin empati ve anlayış</li>
                  <li>• Güçlü sezgisel yetenekler</li>
                  <li>• Sevgi dolu ilişkiler</li>
                  <li>• Duygusal iyileşme gücü</li>
                  <li>• Ruhsal bağlantılar</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">Dikkat Edilmesi Gerekenler</h3>
                <ul className="space-y-1 text-slate-300 text-sm">
                  <li>• Aşırı duygusallık ve dram</li>
                  <li>• Sınırların belirsizliği</li>
                  <li>• İllüzyon ve hayal dünyası</li>
                  <li>• Bağımlı ilişki kalıpları</li>
                  <li>• Duygusal manipülasyon</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4">Yaşam Alanlarındaki Etkiler</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-blue-400 mb-2">Aşk ve İlişkiler</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Romantik bağlantılar, duygusal intimacy, aile ilişkileri ve derin duygusal bağlar.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-400 mb-2">Yaratıcılık</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Sanatsal ifade, müzik, şiir ve duygusal derinlik gerektiren yaratıcı çalışmalar.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-400 mb-2">Ruhsal Gelişim</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Meditasyon, sezgisel gelişim, iyileşme çalışmaları ve ruhsal uyanış süreçleri.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Element Connection */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Su Elementi Bağlantısı</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplets className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Akışkanlık</h3>
              <p className="text-slate-400 text-sm">Esneklik, adaptasyon ve değişime açıklık</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🌊</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Derinlik</h3>
              <p className="text-slate-400 text-sm">Bilinçsiz, gizli duygular ve ruhsal sırlar</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">💧</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Temizlik</h3>
              <p className="text-slate-400 text-sm">Duygusal arınma ve iyileşme süreçleri</p>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Tarot kartları sembolik yorumlama araçlarıdır ve kesinlik iddiası taşımazlar. İçerikler tamamen
            merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/wands"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Değnekler
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/swords"
            className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Kılıçlar
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}